/**
 * Issued letters (spec 8.6, 12): what each letter type is generated from, which register
 * it reads, the printed title of each type, the liber status a marriage reissue prints,
 * and the system margin note a reissue for marriage appends to the baptism entry. Pure;
 * the worker and the screens share it.
 */
import { parsePartialDateInput, tanggalTerlaluJauh } from './dates/partial'
import { type JenisSurat, PENULIS_SISTEM, SURAT_BUKU, type TabelRegister } from './registers'
import { ALASAN_TERBIT_ULANG } from './schema/contract'
import { format, suratStrings, uiStrings } from './strings/index'
import type { CatatanPinggir } from './validators/kolom'

export type AlasanTerbitUlang = (typeof ALASAN_TERBIT_ULANG)[number]

export function isAlasanTerbitUlang(value: unknown): value is AlasanTerbitUlang {
  return typeof value === 'string' && (ALASAN_TERBIT_ULANG as readonly string[]).includes(value)
}

/** What a letter is generated from: a register entry (found through a person), a person, or a family. */
export type SumberSurat = 'entri' | 'umat' | 'keluarga'

export const SUMBER_SURAT: Readonly<Record<JenisSurat, SumberSurat>> = {
  surat_baptis: 'entri',
  surat_komuni: 'entri',
  surat_krisma: 'entri',
  surat_perkawinan: 'entri',
  surat_kematian: 'entri',
  surat_pindah: 'keluarga',
  surat_pengantar_nikah: 'umat',
  kartu_keluarga: 'keluarga',
}

/** The register a letter attests, when it attests one. */
export const TABEL_SURAT: Readonly<Partial<Record<JenisSurat, TabelRegister>>> = Object.fromEntries(
  Object.entries(SURAT_BUKU).map(([tabel, jenis]) => [jenis, tabel]),
) as Partial<Record<JenisSurat, TabelRegister>>

/** "Surat Baptis", the screen label of a type (ui.enum.jenisSurat). */
export function labelSurat(jenis: JenisSurat): string {
  return (uiStrings.enum.jenisSurat as Record<string, string>)[jenis] ?? jenis
}

/** "SURAT BAPTIS", the printed title (surat.*.judul). */
export const JUDUL_SURAT: Readonly<Record<JenisSurat, string>> = {
  surat_baptis: suratStrings.baptis.judul,
  surat_komuni: suratStrings.komuni.judul,
  surat_krisma: suratStrings.krisma.judul,
  surat_perkawinan: suratStrings.perkawinan.judul,
  surat_kematian: suratStrings.kematian.judul,
  surat_pindah: suratStrings.pindah.judul,
  surat_pengantar_nikah: suratStrings.pengantarNikah.judul,
  kartu_keluarga: suratStrings.kartuKeluarga.judul,
}

/**
 * A letter's date as typed (finding C5): a whole day, "28-09-2026", never a year or a month
 * alone (a year read back as "Tahun 2026" made the number "089/SB/-/2026" and the sheet print
 * "Agats, September 2026"); `format` for anything not a whole day. Release 17 (owner,
 * 2026-10-08): the parish prepares letters before the ceremony, so a letter may be dated ahead
 * of today (WIT, `hariIni` canonical) up to a year (`nanti`: the form says so quietly); further
 * is `jauh`. An unknown today refuses nothing and says nothing ahead.
 */
export type TanggalSurat =
  | { ok: true; tanggal: string; nanti: boolean }
  | { ok: false; salah: 'format' | 'jauh' }

export function bacaTanggalSurat(input: string, hariIni: string): TanggalSurat {
  const tanggal = parsePartialDateInput(input)
  if (tanggal === null || tanggal.length !== 10) return { ok: false, salah: 'format' }
  if (hariIni === '') return { ok: true, tanggal, nanti: false }
  if (tanggalTerlaluJauh(tanggal, hariIni)) return { ok: false, salah: 'jauh' }
  return { ok: true, tanggal, nanti: tanggal > hariIni }
}

/** A letter's number carries its sequence: at least one digit ("ABC/xyz" is no number, C5). */
export function nomorSuratBerangka(nomor: string): boolean {
  return /\d/.test(nomor)
}

/**
 * The letters the bishop signs (release 25, owner 2026-10-30): the Komuni Pertama and Krisma
 * keepsakes, which are their official letters. Every other letter is the Pastor Kepala Paroki's.
 */
export const SURAT_USKUP: readonly JenisSurat[] = ['surat_komuni', 'surat_krisma']

/** The letter is signed by the bishop (`SURAT_USKUP`). */
export function ditandatanganiUskup(jenis: JenisSurat): boolean {
  return SURAT_USKUP.includes(jenis)
}

/** Letters whose source is a person's register entry: the "Untuk" search offers people. */
export function sumberAdalahUmat(jenis: JenisSurat): boolean {
  return SUMBER_SURAT[jenis] !== 'keluarga'
}

export interface MasukanLiber {
  /** `umat.status_perkawinan` of the linked person, when linked. */
  statusPerkawinan: string | null
  /** A perkawinan entry of this parish names the person as a spouse. */
  adaEntriPerkawinan: boolean
  /** The baptism entry's margin notes. */
  catatan: readonly CatatanPinggir[]
  /**
   * The entry shows a marriage among its later sacraments (`./sakramenLanjut`): typed on it, or
   * recorded of the person, in SATU's books or in another parish. Absent: none.
   */
  perkawinanTercatat?: boolean
}

/** A marriage notation names the spouse ("menikah dengan …"); issuance notes do not. */
const NOTASI_PERKAWINAN = /\b(menikah|kawin|perkawinan|nikah)\s+dengan\b/i

/** A margin note of the entry records a marriage ("menikah dengan …"). */
export function catatanMencatatPerkawinan(catatan: readonly CatatanPinggir[]): boolean {
  return catatan.some((c) => NOTASI_PERKAWINAN.test(c.teks))
}

/**
 * "Status: liber (belum menikah)" holds when nothing on the device says the person has
 * married in the Church: no marriage entry links them, their civil status is not
 * `menikah_gereja`, the baptism entry shows no marriage among its later sacraments, and no
 * margin note of the baptism records a marriage.
 */
export function statusLiber(masukan: MasukanLiber): boolean {
  if (masukan.adaEntriPerkawinan) return false
  if (masukan.perkawinanTercatat === true) return false
  if (masukan.statusPerkawinan === 'menikah_gereja') return false
  return !catatanMencatatPerkawinan(masukan.catatan)
}

/**
 * Release 32 review M2: the numbers of a note `catatanTerbitUlangPerkawinan` made, or null for any
 * other text: the one note a Pastor Paroki's push may append to a baptism entry. Each number is
 * read as written, without spaces at either end.
 */
export function bacaCatatanTerbitUlangPerkawinan(
  teks: string,
): { nomor: string; lama: string } | null {
  const templat = suratStrings.catatanTerbitUlangPerkawinan
  const pola = templat
    .split(/(\{nomor\}|\{lama\})/)
    .map((bagian) =>
      bagian === '{nomor}' || bagian === '{lama}'
        ? '(\\S(?:.*\\S)?)'
        : bagian.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    )
    .join('')
  const m = new RegExp(`^${pola}$`).exec(teks)
  if (m === null || m[1] === undefined || m[2] === undefined) return null
  return templat.indexOf('{nomor}') < templat.indexOf('{lama}')
    ? { nomor: m[1], lama: m[2] }
    : { nomor: m[2], lama: m[1] }
}

/**
 * The note a reissue for marriage appends to the baptism entry (canon 535 §2 practice:
 * the register records that the certificate was issued for marriage). Signed "Sistem".
 */
export function catatanTerbitUlangPerkawinan(
  tanggal: string,
  nomorBaru: string,
  nomorLama: string,
): CatatanPinggir {
  return {
    tanggal,
    teks: format(suratStrings.catatanTerbitUlangPerkawinan, { nomor: nomorBaru, lama: nomorLama }),
    oleh: PENULIS_SISTEM,
  }
}
